/**
 * Step 6 — ball (owner: ball/passing team):
 *  - HELD (and DEAD with a holder): rides in the holder's glove (tucked against the head while the glove throws a
 *    stiff-arm Fist), nose tilted toward the facing direction, velocity = the holder's.
 *  - AIR: flight (ballGravity, Hail Mary float, rules.windX, spiral / tumble), goalposts (DOINK), then catches /
 *    interceptions / swats (live phases only). Ground contact ends the flight (DEAD, holder -1; the rules call it
 *    incomplete or FG short/post on the same tick). The FG window itself is judged by the rules.
 *  - DEAD (holder -1): ballistic with bounces and rolling, no events except BALL_BOUNCE.
 *  - LOOSE (a Classic kick on the ground): bounces / rolls like a dead ball, and is picked up by the first player
 *    allowed to (ball/loose.ts).
 * The throw itself is launched by the controller (ball/launch.ts) on THROW_RELEASE's last startup frame.
 */
import { MOTION } from '@hf/shared';
import { BallState, GlovePose, PlayerState } from '../enums';
import type { SimEventSink } from '../sink';
import type { SimState } from '../state';
import { catchOrSwat } from './catch';
import { flyBall } from './flight';
import { recoverLoose } from './loose';
import { ballInPlayAir, looseInPlay } from './reach';

const followHolder = (s: SimState): void => {
  const b = s.ball;
  const p = s.players[b.holder as 0 | 1];
  const tucked = p.state === PlayerState.STIFF_ARM && p.glovePose === GlovePose.FIST;
  const ox = tucked ? MOTION.tuckOx : p.gloveX;
  const oy = tucked ? MOTION.tuckOy : p.gloveY;
  b.x = p.x + p.facing * ox;
  b.y = p.y + oy;
  b.vx = p.vx;
  b.vy = p.vy;
  b.angle = p.facing * MOTION.heldBallTilt;
  b.angVel = 0;
};

export const updateBall = (s: SimState, ev: SimEventSink): void => {
  const b = s.ball;
  if ((b.state === BallState.HELD || b.state === BallState.DEAD) && b.holder >= 0) {
    followHolder(s);
    return;
  }
  if (b.state === BallState.HELD) return; // no holder (blank snapshot buffer): nothing to simulate
  const live = ballInPlayAir(s);
  flyBall(s, ev, live);
  if (b.state === BallState.AIR && live) catchOrSwat(s, ev);
  else if (looseInPlay(s)) recoverLoose(s, ev);
};
